import { useState, useEffect } from 'preact/hooks';
import { getAuditLogs, getUsers } from '../lib/api';
import { useAuth } from '../lib/auth';
import { FeatureLocked } from '../components/FeatureLocked';

const SUPER_ADMIN_EMAIL = 'dthake@ms.vanilla.net.mt';

// Action type labels and colors
const ACTION_LABELS = {
  'user.updated': { label: 'User Updated', color: '#3B82F6' },
  'user.deleted': { label: 'User Deleted', color: '#EF4444' },
  'user.imported': { label: 'User Imported', color: '#10B981' },
  'leave.requested': { label: 'Leave Requested', color: '#8B5CF6' },
  'leave.approved': { label: 'Leave Approved', color: '#10B981' },
  'leave.rejected': { label: 'Leave Rejected', color: '#EF4444' },
  'leave.cancelled': { label: 'Leave Cancelled', color: '#F59E0B' },
  'leave_type.created': { label: 'Leave Type Created', color: '#10B981' },
  'leave_type.updated': { label: 'Leave Type Updated', color: '#3B82F6' },
  'leave_type.deleted': { label: 'Leave Type Deleted', color: '#EF4444' },
  'balance.adjusted': { label: 'Balance Adjusted', color: '#F59E0B' },
  'department.created': { label: 'Department Created', color: '#10B981' },
  'department.updated': { label: 'Department Updated', color: '#3B82F6' },
  'department.deleted': { label: 'Department Deleted', color: '#EF4444' },
  'audit_log.deleted': { label: 'Audit Log Deleted', color: '#EF4444' }
};

export function AuditTrailPage({ user }) {
  const { hasFeature } = useAuth();

  // Check for audit_trail feature access
  if (!hasFeature('audit_trail')) {
    return (
      <FeatureLocked
        feature="Audit Trail"
        description="View complete history of all changes made in your workspace"
      />
    );
  }

  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    user_id: ''
  });
  const [expandedLog, setExpandedLog] = useState(null);
  const limit = 25;

  const isSuperAdmin = user?.email === SUPER_ADMIN_EMAIL;

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [page, filters]);

  async function loadUsers() {
    try {
      const data = await getUsers('all');
      setUsers(data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }

  async function loadLogs() {
    try {
      setLoading(true);
      const params = {
        limit: limit.toString(),
        offset: (page * limit).toString()
      };
      if (filters.action) params.action = filters.action;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.user_id) params.user_id = filters.user_id;

      const data = await getAuditLogs(params);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function getActionInfo(action) {
    return ACTION_LABELS[action] || { label: action, color: '#6B7280' };
  }

  function renderJsonValue(value) {
    if (!value) return <span style={{ color: '#9CA3AF' }}>-</span>;
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return (
        <pre style={{
          margin: 0,
          fontSize: '12px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          backgroundColor: '#F3F4F6',
          padding: '8px',
          borderRadius: '4px',
          maxHeight: '200px',
          overflow: 'auto'
        }}>
          {JSON.stringify(parsed, null, 2)}
        </pre>
      );
    } catch {
      return <span>{String(value)}</span>;
    }
  }

  const totalPages = Math.ceil(total / limit);

  // Get unique action types for filter
  const actionTypes = [...new Set(Object.keys(ACTION_LABELS))];

  // Get unique entity types for filter
  const entityTypes = ['user', 'leave_request', 'leave_type', 'leave_balance', 'department', 'audit_log'];

  return (
    <div>
      <div class="bento-page-header-row">
        <div class="bento-page-header">
          <h1 class="bento-page-title">Audit Trail</h1>
          <p class="bento-page-subtitle">
            View all changes made in your workspace {isSuperAdmin && <span style={{ color: 'var(--success)' }}>(Super Admin)</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <span class="badge badge-pending">
            {total} total entries
          </span>
        </div>
      </div>

      {/* Filters */}
      <div class="bento-card bento-filters-card" style={{ gap: '12px', marginBottom: '1.5rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Action Type</label>
          <select
            value={filters.action}
            onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPage(0); }}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', minWidth: '150px' }}
          >
            <option value="">All Actions</option>
            {actionTypes.map(action => (
              <option key={action} value={action.split('.')[0]}>
                {ACTION_LABELS[action]?.label || action}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Entity Type</label>
          <select
            value={filters.entity_type}
            onChange={(e) => { setFilters({ ...filters, entity_type: e.target.value }); setPage(0); }}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', minWidth: '150px' }}
          >
            <option value="">All Entities</option>
            {entityTypes.map(type => (
              <option key={type} value={type}>{type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Changed By</label>
          <select
            value={filters.user_id}
            onChange={(e) => { setFilters({ ...filters, user_id: e.target.value }); setPage(0); }}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', minWidth: '180px' }}
          >
            <option value="">All Users</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
          <button
            onClick={() => { setFilters({ action: '', entity_type: '', user_id: '' }); setPage(0); }}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #D1D5DB',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Audit Log Table */}
      {loading ? (
        <div class="bento-card" style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
          Loading audit logs...
        </div>
      ) : logs.length === 0 ? (
        <div class="bento-card" style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
          No audit logs found
        </div>
      ) : (
        <div class="bento-card bento-table-card" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontSize: '14px', fontWeight: 600 }}>Timestamp</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontSize: '14px', fontWeight: 600 }}>Action</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontSize: '14px', fontWeight: 600 }}>Changed By</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontSize: '14px', fontWeight: 600 }}>Entity</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #E5E7EB', fontSize: '14px', fontWeight: 600 }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => {
                const actionInfo = getActionInfo(log.action);
                const isExpanded = expandedLog === log.id;

                return (
                  <>
                    <tr
                      key={log.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB',
                        cursor: 'pointer'
                      }}
                      onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                    >
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', fontSize: '14px' }}>
                        {formatDate(log.created_at)}
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: actionInfo.color + '20',
                          color: actionInfo.color,
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          {actionInfo.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', fontSize: '14px' }}>
                        <div>
                          <div style={{ fontWeight: 500 }}>{log.user_name || 'System'}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{log.user_email || ''}</div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', fontSize: '14px' }}>
                        <div>
                          <div style={{ fontWeight: 500 }}>{log.entity_type?.replace('_', ' ')}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'monospace' }}>
                            {log.entity_id ? log.entity_id.substring(0, 20) + (log.entity_id.length > 20 ? '...' : '') : '-'}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', textAlign: 'center' }}>
                        <button
                          style={{
                            padding: '4px 12px',
                            borderRadius: '4px',
                            border: '1px solid #D1D5DB',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {isExpanded ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${log.id}-details`}>
                        <td colSpan="5" style={{ padding: '16px', backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Previous Value</div>
                              {renderJsonValue(log.old_value)}
                            </div>
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>New Value</div>
                              {renderJsonValue(log.new_value)}
                            </div>
                          </div>
                          {(log.ip_address || log.user_agent) && (
                            <div style={{ marginTop: '12px', fontSize: '12px', color: '#6B7280' }}>
                              {log.ip_address && <div>IP: {log.ip_address}</div>}
                              {log.user_agent && <div style={{ marginTop: '4px', wordBreak: 'break-all' }}>User Agent: {log.user_agent}</div>}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #D1D5DB',
              backgroundColor: page === 0 ? '#F3F4F6' : 'white',
              cursor: page === 0 ? 'not-allowed' : 'pointer',
              color: page === 0 ? '#9CA3AF' : '#374151'
            }}
          >
            Previous
          </button>
          <span style={{ padding: '8px 16px', color: '#6B7280' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #D1D5DB',
              backgroundColor: page >= totalPages - 1 ? '#F3F4F6' : 'white',
              cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
              color: page >= totalPages - 1 ? '#9CA3AF' : '#374151'
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Note about immutability */}
      <div class="bento-card" style={{
        marginTop: 'var(--bento-gap)',
        backgroundColor: '#FEF3C7',
        border: '1px solid #FCD34D'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{ fontSize: '20px' }}>&#128274;</span>
          <div>
            <div style={{ fontWeight: 600, color: '#92400E', marginBottom: '4px' }}>Immutable Audit Trail</div>
            <div style={{ fontSize: '14px', color: '#92400E' }}>
              This audit trail is read-only and cannot be modified by workspace administrators.
              {isSuperAdmin
                ? ' As a super admin, you have special privileges to manage this data if needed.'
                : ' Only the system super admin (dthake@ms.vanilla.net.mt) can delete entries if absolutely necessary.'
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
